/**
 * The title of a react-admin form page, on the canvas above the form's card
 * (hybrid-3c §6: one PageTitle on every screen, title and actions above the
 * card). Pass it as the `actions` of `<Edit>` / `<Create>`, which render it
 * right before the card.
 */

import React from 'react';
import { useResourceContext, useTranslate } from 'react-admin';
import { Box } from '@mui/material';
import { FORM_MAX_WIDTH } from '@/lightTheme';
import { PageTitle } from './PageTitle';

export interface FormPageTitleProps {
  mode: 'create' | 'edit';
  /** Defaults to the resource in context. */
  resource?: string;
  /** Replaces the generated "Crear …" / "Editar …" title. */
  title?: string;
  /** One line under the title: what the record is for, or which record it is. */
  subtitle?: string;
}

const lowerFirst = (value: string): string => value.charAt(0).toLocaleLowerCase() + value.slice(1);

export const FormPageTitle: React.FC<FormPageTitleProps> = ({ mode, resource: resourceProp, title, subtitle }) => {
  const translate = useTranslate();
  const contextResource = useResourceContext();
  const resource = resourceProp ?? contextResource;
  const name = lowerFirst(translate(`resources.${resource}.name`, { smart_count: 1, _: resource }));

  return (
    // On a phone the page has no top inset under the app bar; the title takes the section
    // header's own 12px so the two kinds of page start at the same height.
    <Box sx={{ pt: { xs: 1.5, sm: 0 }, pb: 2, maxWidth: FORM_MAX_WIDTH }}>
      <PageTitle title={title ?? translate(`shell.form.${mode}`, { name })} subtitle={subtitle} />
    </Box>
  );
};

export default FormPageTitle;
